import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardAPI } from '../../services/api';
import StatCard from './shared/StatCard';
import QuickActionButton from './shared/QuickActionButton';
import './SchoolDashboard.css';
import Swal from 'sweetalert2';
import html2canvas from 'html2canvas';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

const SchoolDashboard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGrades, setExpandedGrades] = useState({});

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardAPI.getDashboard();
      console.log('Dashboard API Response:', JSON.stringify(response, null, 2));

      // Extract data from ApiResponse wrapper
      const data = response.data || response;
      console.log('Dashboard Data:', data);
      console.log('Overview:', data.overview);
      console.log('SchoolInfo:', data.schoolInfo);
      console.log('GradeBreakdown:', data.gradeBreakdown);
      console.log('HasManageSchoolPermission:', data.hasManageSchoolPermission);

      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleGradeExpand = (tahun) => {
    setExpandedGrades(prev => ({
      ...prev,
      [tahun]: !prev[tahun]
    }));
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>{t('dashboard:loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>{t('dashboard:error')}: {error}</p>
        <button onClick={loadDashboardData} className="btn btn-primary" title={t('dashboard:retry', { lng: 'en' })}>
          <i className="fas fa-redo"></i> {t('dashboard:retry')}
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('dashboard:screenshot.capturing'),
        html: t('dashboard:screenshot.wait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('dashboard:screenshot.captureFailed'),
        text: t('dashboard:screenshot.captureError')
      });
    }
  };

  const { schoolInfo, overview, gradeBreakdown, uniformOverview, activitiesStatus, recentActivities, systemUserActivities, uniformDataActivities, hasManageSchoolPermission } = dashboardData;

  return (
    <div className="school-dashboard">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2 title={t('dashboard:title', { lng: 'en' })}>{t('dashboard:title')}</h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('dashboard:reportIssue', { lng: 'en' })}>
          <i className="fas fa-bug"></i>
        </button>
      </div>

      {/* School Info */}
      <div className="school-info-card">
        <div className="school-info-row">
          <div>
            <strong title={t('dashboard:schoolInfo.school', { lng: 'en' })}>{t('dashboard:schoolInfo.school')}:</strong> {schoolInfo?.schoolCode} - {schoolInfo?.schoolName}
          </div>
          <div>
            <strong title={t('dashboard:schoolInfo.district', { lng: 'en' })}>{t('dashboard:schoolInfo.district')}:</strong> {schoolInfo?.ppd || t('dashboard:schoolInfo.notAvailable')}
          </div>
          <div>
            <strong title={t('dashboard:schoolInfo.dun', { lng: 'en' })}>{t('dashboard:schoolInfo.dun')}:</strong> {schoolInfo?.dun || t('dashboard:schoolInfo.notAvailable')}
          </div>
          <div>
            <strong title={t('dashboard:schoolInfo.parliament', { lng: 'en' })}>{t('dashboard:schoolInfo.parliament')}:</strong> {schoolInfo?.par || t('dashboard:schoolInfo.notAvailable')}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          icon="fa-users"
          value={overview?.totalStudents || 0}
          label={t('dashboard:stats.totalStudents')}
          color="primary"
        />
        <StatCard
          icon="fa-male"
          value={overview?.maleStudents || 0}
          label={t('dashboard:stats.maleStudents')}
          color="info"
        />
        <StatCard
          icon="fa-female"
          value={overview?.femaleStudents || 0}
          label={t('dashboard:stats.femaleStudents')}
          color="success"
        />
        <StatCard
          icon="fa-calendar-check"
          value={overview?.activeActivities || 0}
          label={t('dashboard:stats.activeActivities')}
          color="warning"
        />
      </div>

      <div className="dashboard-content">
        {/* Quick Actions */}
        <div className="dashboard-section">
          <h3 title={t('dashboard:quickActions.title', { lng: 'en' })}><i className="fas fa-bolt"></i> {t('dashboard:quickActions.title')}</h3>
          <div className="quick-actions-grid">
            <QuickActionButton
              icon="fa-users"
              label={t('dashboard:quickActions.studentMaster')}
              path="/student-master"
              color="primary"
            />
            <QuickActionButton
              icon="fa-shirt"
              label={t('dashboard:quickActions.studentUniform')}
              path="/student-uniform"
              color="success"
            />
            <QuickActionButton
              icon="fa-school"
              label={t('dashboard:quickActions.schoolReport')}
              path="/report-school"
              color="warning"
            />
            <QuickActionButton
              icon="fa-list-alt"
              label={t('dashboard:quickActions.studentListing')}
              path="/report-student-listing"
              color="info"
            />
          </div>
          <div className="download-buttons-row">
            <a className="download-icon-btn download-pdf" href="/docs/Sukatan Pakaian dan Kasut 2026 v2.pdf" download title="Sukatan Pakaian & Kasut 2026">
              <i className="fas fa-file-pdf"></i>
            </a>
            <a className="download-icon-btn download-excel" href="/docs/KTC-Student-Input-Form-Template 2026.xlsx" download title="Borang Input Pelajar 2026">
              <i className="fas fa-file-excel"></i>
            </a>
            <a className="download-icon-btn download-word" href="/docs/KTC SYSTEM(Headmaster).docx" download title="Manual Pengguna (Guru Besar)">
              <i className="fas fa-file-word"></i>
            </a>
          </div>
        </div>

        {/* Students by Grade & Class */}
        <div className="dashboard-section dashboard-section-double">
          <h3 title={t('dashboard:gradeBreakdown.title', { lng: 'en' })}>
            <i className="fas fa-layer-group"></i> {t('dashboard:gradeBreakdown.title')}
            <span className="eligibility-info"> - {new Date().getFullYear()} ('WARGANEGARA', 'PEMASTAUTIN TETAP')</span>
          </h3>
          <div className="grade-summary-container">
            {gradeBreakdown && gradeBreakdown.length > 0 ? (
              <table className="grade-summary-table">
                <thead>
                  <tr>
                    <th title={t('dashboard:gradeBreakdown.tahun', { lng: 'en' })}>{t('dashboard:gradeBreakdown.tahun')}</th>
                    <th title={t('dashboard:gradeBreakdown.male', { lng: 'en' })}>{t('dashboard:gradeBreakdown.male')}</th>
                    <th title={t('dashboard:gradeBreakdown.female', { lng: 'en' })}>{t('dashboard:gradeBreakdown.female')}</th>
                    <th title={t('dashboard:gradeBreakdown.total', { lng: 'en' })}>{t('dashboard:gradeBreakdown.total')}</th>
                    <th title={t('dashboard:gradeBreakdown.status', { lng: 'en' })}>{t('dashboard:gradeBreakdown.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {gradeBreakdown.map(grade => (
                    <tr key={grade.tahun}>
                      <td className="grade-cell-tahun">
                        <strong>{grade.tahunText || grade.tahun}</strong>
                      </td>
                      <td className="grade-cell-number">{grade.maleStudents}</td>
                      <td className="grade-cell-number">{grade.femaleStudents}</td>
                      <td className="grade-cell-total">
                        <strong>{grade.totalStudents}</strong>
                      </td>
                      <td className="grade-cell-status">
                        <span className={`status-badge ${grade.status && grade.status.toUpperCase() === 'CLOSED' ? 'status-closed' : 'status-open'}`}>
                          {grade.status ? grade.status.toUpperCase() : 'OPEN'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">{t('dashboard:gradeBreakdown.noData')}</p>
            )}
          </div>
        </div>

        {/* Two Audit Log Tables Side by Side */}
        <div className="audit-logs-row">
          {/* System/User Activities - LOGIN_SUCCESS, etc. */}
          <div className="dashboard-section dashboard-section-half">
            <h3 title={t('dashboard:systemActivities.title', { lng: 'en' })}><i className="fas fa-user-clock"></i> {t('dashboard:systemActivities.title')}</h3>
            <div className="audit-trail-container">
              {systemUserActivities && systemUserActivities.length > 0 ? (
                <table className="audit-trail-table">
                  <thead>
                    <tr>
                      <th title={t('dashboard:recentActivities.datetime', { lng: 'en' })}>{t('dashboard:recentActivities.datetime')}</th>
                      <th title={t('dashboard:recentActivities.action', { lng: 'en' })}>{t('dashboard:recentActivities.action')}</th>
                      <th title={t('dashboard:recentActivities.user', { lng: 'en' })}>{t('dashboard:recentActivities.user')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemUserActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td className="audit-datetime">
                          <div className="audit-date">{new Date(activity.timestamp).toLocaleDateString('en-MY')}</div>
                          <div className="audit-time">{new Date(activity.timestamp).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}</div>
                        </td>
                        <td className="audit-action-cell">
                          <span className={`action-badge action-${(activity.actionType || 'unknown').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                            {activity.actionType || t('dashboard:recentActivities.unknownAction')}
                          </span>
                        </td>
                        <td className="audit-user-cell">
                          <div className="audit-user-name">{activity.userName || activity.userEmail || t('dashboard:recentActivities.system')}</div>
                          {activity.userRole && (
                            <div className="audit-user-role">{activity.userRole}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">{t('dashboard:recentActivities.noActivities')}</p>
              )}
            </div>
          </div>

          {/* Uniform Data Activities - SAVE, SUBMIT, REOPEN */}
          <div className="dashboard-section dashboard-section-half">
            <h3 title={t('dashboard:uniformActivities.title', { lng: 'en' })}><i className="fas fa-tshirt"></i> {t('dashboard:uniformActivities.title')}</h3>
            <div className="audit-trail-container">
              {uniformDataActivities && uniformDataActivities.length > 0 ? (
                <table className="audit-trail-table">
                  <thead>
                    <tr>
                      <th title={t('dashboard:recentActivities.datetime', { lng: 'en' })}>{t('dashboard:recentActivities.datetime')}</th>
                      <th title={t('dashboard:recentActivities.action', { lng: 'en' })}>{t('dashboard:recentActivities.action')}</th>
                      <th title={t('dashboard:recentActivities.details', { lng: 'en' })}>{t('dashboard:recentActivities.details')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uniformDataActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td className="audit-datetime">
                          <div className="audit-date">{new Date(activity.timestamp).toLocaleDateString('en-MY')}</div>
                          <div className="audit-time">{new Date(activity.timestamp).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}</div>
                        </td>
                        <td className="audit-action-cell">
                          <span className={`action-badge action-${(activity.actionType || 'unknown').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                            {activity.actionType || t('dashboard:recentActivities.unknownAction')}
                          </span>
                        </td>
                        <td className="audit-details-cell">
                          {activity.details || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">{t('dashboard:uniformActivities.noActivities')}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: t('dashboard:context.screenName'),
            schoolInfo,
            overview,
            dashboardData
          }}
        />
      )}
    </div>
  );
};

export default SchoolDashboard;
